import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React from "react";
import { twMerge } from "tailwind-merge";

import { Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { EhrTargetRow } from "@pages/ClinicalReportDetails/SelectProgramming/EhrTargetRow";
import { GoalStatusLabel } from "@pages/ClinicalReportDetails/SelectProgramming/GoalStatusLabel";
import { GoalToggleState } from "@pages/ClinicalReportDetails/SelectProgramming/useSelectProgramming";

interface EhrGoalRowProps {
  goal: GoalToggleState;
  onToggleGoal: () => void;
  onToggleTarget: (targetId: string) => void;
  onToggleExpand: () => void;
  showTargets: boolean;
}

export const EhrGoalRow: React.FC<EhrGoalRowProps> = ({
  goal,
  onToggleGoal,
  onToggleTarget,
  onToggleExpand,
  showTargets,
}) => {
  const hasTargets = goal.targets.length > 0;
  const { captureGoalToggled, captureGoalExpanded } = useEhrAnalytics();

  const handleToggleGoal = () => {
    captureGoalToggled(goal.goalId, !goal.checked);
    onToggleGoal();
  };

  const handleToggleExpand = () => {
    captureGoalExpanded(goal.goalId, !goal.expanded);
    onToggleExpand();
  };

  return (
    <div>
      <div className="flex items-center py-2">
        <input
          type="checkbox"
          checked={goal.checked}
          onChange={handleToggleGoal}
          disabled={goal.disabled}
          aria-label="Select goal"
          aria-describedby={`goal-title-${goal.goalId}`}
          className="h-4 w-4 shrink-0 rounded-sm border-limestone-300 text-green-700 focus:ring-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
        />
        {showTargets ? (
          <button
            type="button"
            onClick={handleToggleExpand}
            aria-label={goal.expanded ? "Collapse targets" : "Expand targets"}
            aria-expanded={hasTargets ? goal.expanded : undefined}
            className={twMerge("ml-1 flex items-center justify-center w-6 h-6 shrink-0", !hasTargets && "w-3")}
            disabled={!hasTargets}
          >
            {hasTargets ? (
              goal.expanded ? (
                <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-text-secondary" />
              )
            ) : (
              <span className="w-2" />
            )}
          </button>
        ) : (
          <span className="w-2" />
        )}
        <span id={`goal-title-${goal.goalId}`} className="ml-1 flex-1 text-sm text-text-primary truncate ph-no-capture">
          {goal.title}
        </span>
        <GoalStatusLabel status={goal.status} className="ml-2 shrink-0" />
      </div>

      {goal.expanded && (
        <>
          {!!goal.targets.length && goal.checked && showTargets && (
            <div className="flex items-center gap-1 py-2 pl-6 pr-0">
              <Small>
                {goal.targets.length} target{goal.targets.length !== 1 ? "s" : ""} included in goal graph
              </Small>
              <Tooltip
                placement="top"
                infoIconClassName="w-4! h-4!"
                text={`This goal's graph includes all ${goal.targets.length} target${goal.targets.length !== 1 ? "s" : ""} data by default. Toggle to optionally include individual target graphs in the exported report.`}
              />
            </div>
          )}
          {showTargets &&
            goal.targets.map((target) => (
              <EhrTargetRow key={target.targetId} target={target} onToggle={() => onToggleTarget(target.targetId)} />
            ))}
        </>
      )}
    </div>
  );
};
